import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';

// Settings → Organisations (super admin only). Reworked from the old
// AdminTenants page: the wide eight-column table collapses to five, and
// the per-tenant controls — discovery-region subscriptions and the
// activate/deactivate flow with its reason dialog — live behind Manage.

const STROKE = 2.75;

// Mirrors backend services/regions.py — slug order drives display order.
// Each region has its own weekly discovery cron; toggling a region here
// subscribes the tenant to that cron's run.
const DISCOVERY_REGIONS = [
  { slug: 'north_america', label: 'North America' },
  { slug: 'south_america', label: 'South America' },
  { slug: 'europe', label: 'Europe' },
  { slug: 'africa_middle_east', label: 'Africa / Middle East' },
  { slug: 'asia_pacific', label: 'Asia Pacific' },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function relativeAge(value) {
  if (!value) return 'never';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function OrganisationsTab() {
  const { user, isSuperAdmin } = useAuth();
  const isSuper = isSuperAdmin();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [manageId, setManageId] = useState(null);
  // Modal state for the deactivate flow — a reason is captured on
  // disable, but reactivation is one-click (no modal).
  const [deactivating, setDeactivating] = useState(null); // { org, reason }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listOrganizations();
      setOrgs(data.organizations || []);
    } catch (e) {
      setError(e.message || 'Failed to load organisations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuper) load(); }, [load, isSuper]);

  // Super admin only — the tab is never offered to org admins, and the
  // backend rejects the calls anyway; this is defence in depth.
  if (!isSuper) return null;

  async function applyPatch(orgId, patch) {
    setBusyId(orgId);
    try {
      const updated = await api.updateOrganization(orgId, patch);
      setOrgs((prev) => prev.map((o) => (o.id === orgId ? { ...o, ...updated } : o)));
    } catch (e) {
      setError(e.message || 'Failed to update organisation');
    } finally {
      setBusyId(null);
    }
  }

  function toggleRegion(org, slug) {
    const current = org.discovery_regions || [];
    const next = current.includes(slug)
      ? current.filter((s) => s !== slug)
      : [...current, slug];
    applyPatch(org.id, { discovery_regions: next });
  }

  async function submitDeactivate() {
    if (!deactivating) return;
    const { org, reason } = deactivating;
    await applyPatch(org.id, {
      is_active: false,
      deactivated_reason: reason.trim() || null,
    });
    setDeactivating(null);
  }

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div>
          <h4 className="o-card-head-title">Organisations</h4>
          <p className="o-card-head-sub">
            Each organisation keeps its own brands, tags, scoring and billing.
            Deactivated organisations can't sign in.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} onClick={load} disabled={loading}>
          <RefreshCw size={14} strokeWidth={STROKE} /> Refresh
        </button>
      </div>

      {error && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-accent-700)' }}>Error: {error}</p>
      )}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading organisations…
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Organisation</th>
              <th style={{ textAlign: 'right' }}>Brands</th>
              <th style={{ textAlign: 'right' }}>Members</th>
              <th>Last login</th>
              <th>Status</th>
              <th aria-label="Manage" />
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', opacity: 0.65, padding: 'var(--space-4)' }}>
                  No organisations yet.
                </td>
              </tr>
            )}
            {orgs.map((org) => {
              const isSelf = String(user?.org_id || '') === String(org.id);
              const busy = busyId === org.id;
              const open = manageId === org.id;
              return (
                <React.Fragment key={org.id}>
                  <tr>
                    <td style={{ fontWeight: 600 }}>{org.name || `org ${org.id}`}</td>
                    <td style={{ textAlign: 'right' }}>{org.brand_count}</td>
                    <td style={{ textAlign: 'right' }}>{org.user_count}</td>
                    <td title={formatDate(org.last_login_at)}>{relativeAge(org.last_login_at)}</td>
                    <td>
                      {org.is_active ? (
                        <span className="tag tag-accent-2">Active</span>
                      ) : (
                        <span className="tag tag-neutral" title={org.deactivated_reason || ''}>
                          Suspended{org.deactivated_at ? ` · ${relativeAge(org.deactivated_at)}` : ''}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: 12 }}
                        aria-expanded={open}
                        onClick={() => setManageId(open ? null : org.id)}
                      >
                        Manage {open
                          ? <ChevronUp size={13} strokeWidth={STROKE} />
                          : <ChevronDown size={13} strokeWidth={STROKE} />}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--color-neutral-200)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-2)' }}>
                          <div>
                            <span className="card-kicker">Discovery regions</span>
                            <div className="o-chip-row" style={{ marginTop: 6 }}>
                              {DISCOVERY_REGIONS.map(({ slug, label }) => {
                                const checked = (org.discovery_regions || []).includes(slug);
                                return (
                                  <button
                                    key={slug}
                                    type="button"
                                    className={checked ? 'tag tag-accent-2' : 'tag tag-outline'}
                                    aria-pressed={checked}
                                    disabled={busy}
                                    style={busy ? { cursor: 'wait', opacity: 0.6 } : undefined}
                                    onClick={() => toggleRegion(org, slug)}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                            {(org.discovery_regions || []).length === 0 && (
                              <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
                                Automated discovery off
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, opacity: 0.65 }} title="Credits consumed in the last 7 days">
                              {org.credits_used_7d ?? 0} credits used in 7 days · id {org.id}
                            </span>
                            <span style={{ marginLeft: 'auto' }}>
                              {org.is_active ? (
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  style={{ fontSize: 13 }}
                                  disabled={busy}
                                  title="Deactivate organisation"
                                  onClick={() => setDeactivating({ org, reason: '', isSelf })}
                                >
                                  <ShieldOff size={14} strokeWidth={STROKE} /> Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{ fontSize: 13 }}
                                  disabled={busy}
                                  onClick={() => applyPatch(org.id, { is_active: true })}
                                >
                                  <ShieldCheck size={14} strokeWidth={STROKE} /> Reactivate
                                </button>
                              )}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {deactivating && (
        <div className="dialog-backdrop" onClick={() => setDeactivating(null)}>
          <div className="dialog" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-title">
              Deactivate {deactivating.org.name || `org ${deactivating.org.id}`}?
            </div>
            <div className="dialog-body">
              Users in this organisation will be blocked from signing in until you
              reactivate it. They'll see the reason below on the suspended screen.
              Its weekly sync, discovery, recap and monthly research crons stop
              running, so it stops consuming credits.
            </div>
            {deactivating.isSelf && (
              <div className="dialog-body" style={{ color: 'var(--color-accent-700)' }}>
                This is your own organisation. You keep access as a super admin —
                the suspension gate doesn't apply to you — but every other member
                is signed out until you reactivate it.
              </div>
            )}
            <textarea
              className="input"
              value={deactivating.reason}
              onChange={(e) => setDeactivating({ ...deactivating, reason: e.target.value })}
              placeholder="Reason (optional, e.g. unpaid invoice)"
              rows={3}
              maxLength={255}
            />
            <div className="dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeactivating(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submitDeactivate}>
                <ShieldOff size={14} strokeWidth={STROKE} /> Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganisationsTab;
